import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useClub } from "../../../contexts/ClubContext";
import { useSchemes } from "../../../contexts/SchemesContext";
import { PersonnelGroup, Side } from "../../../types/DepthChartTypes";
import {
  OffensivePassConcept,
  OffensivePassProtectionConcept,
  OffensivePlayTag,
  OffensiveRunConcept,
  SchemeFormation,
  FormationTag,
} from "../../../types/SchemesTypes";
import { RunConcepts } from "./RunConcepts";

// TabPanel component for tab content
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scheme-tabpanel-${index}`}
      aria-labelledby={`scheme-tab-${index}`}
      {...other}
      style={{ padding: "16px 0" }}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const OffenseSchemes = () => {
  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Club context
  const { club } = useClub();

  // Schemes context
  const {
    formations,
    formationTags,
    offensivePlayTags,
    offensivePersonnel,
    runConcepts,
    passConcepts,
    passProtectionConcepts,
    saveFormation,
    saveFormationTag,
    deleteFormationTag,
    savePlayTag,
    deletePlayTag,
    savePersonnelGroup,
    deletePersonnelGroup,
    deleteFormation,
    saveRunConcept,
    savePassConcept,
    savePassProtectionConcept,
    deleteRunConcept,
    deletePassConcept,
    deletePassProtectionConcept,
  } = useSchemes();

  // Dialog states
  const [personnelDialogOpen, setPersonnelDialogOpen] = useState(false);
  const [formationDialogOpen, setFormationDialogOpen] = useState(false);
  const [formationTagDialogOpen, setFormationTagDialogOpen] = useState(false);
  const [playTagDialogOpen, setPlayTagDialogOpen] = useState(false);
  const [runConceptDialogOpen, setRunConceptDialogOpen] = useState(false);
  const [passConceptDialogOpen, setPassConceptDialogOpen] = useState(false);
  const [passProtectionConceptDialogOpen, setPassProtectionConceptDialogOpen] =
    useState(false);

  // Form states
  const [currentPersonnelGroup, setCurrentPersonnelGroup] = useState<
    Partial<PersonnelGroup>
  >({ name: "", side: "OFFENSE" });
  const [currentFormation, setCurrentFormation] = useState<
    Partial<SchemeFormation>
  >({ name: "", club: club });
  const [currentFormationTag, setCurrentFormationTag] = useState<
    Partial<FormationTag>
  >({ name: "", description: "", club: club });
  const [currentPlayTag, setCurrentPlayTag] = useState<
    Partial<OffensivePlayTag>
  >({ name: "", description: "", club: club });
  const [currentRunConcept, setCurrentRunConcept] = useState<
    Partial<OffensiveRunConcept>
  >({ 
    name: "", 
    description: "", 
    club: club,
    code_word_left: "",
    code_word_right: ""
  });
  const [currentPassConcept, setCurrentPassConcept] = useState<
    Partial<OffensivePassConcept>
  >({ name: "", description: "", club: club.id });
  const [currentPassProtectionConcept, setCurrentPassProtectionConcept] =
    useState<Partial<OffensivePassProtectionConcept>>({
      name: "",
      description: "",
      club: club.id,
    });
  // Alert state
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle save personnel group
  const handleSavePersonnelGroup = async () => {
    try {
      if (!currentPersonnelGroup.name) {
        setAlert({
          open: true,
          message: "Name is required",
          severity: "error",
        });
        return;
      }

      await savePersonnelGroup({
        id: currentPersonnelGroup.id,
        name: currentPersonnelGroup.name,
        side: "OFFENSE",
      });

      setAlert({
        open: true,
        message: "Personnel group saved successfully",
        severity: "success",
      });

      setPersonnelDialogOpen(false);
    } catch (error) {
      console.error("Error saving personnel group:", error);
      setAlert({
        open: true,
        message: "Failed to save personnel group",
        severity: "error",
      });
    }
  };

  // Handle save formation
  const handleSaveFormation = async () => {
    try {
      await saveFormation({
        ...(currentFormation as SchemeFormation),
      });

      setAlert({
        open: true,
        message: "Formation saved successfully",
        severity: "success",
      });

      setFormationDialogOpen(false);
    } catch (error) {
      console.error("Error saving formation:", error);
      setAlert({
        open: true,
        message: "Failed to save formation",
        severity: "error",
      });
    }
  };

  // Handle save formation tag
  const handleSaveFormationTag = async () => {
    try {
      if (!currentFormationTag.name) {
        setAlert({
          open: true,
          message: "Name is required",
          severity: "error",
        });
        return;
      }

      await saveFormationTag({
        id: currentFormationTag.id,
        name: currentFormationTag.name,
        description: currentFormationTag.description || null,
      });

      setAlert({
        open: true,
        message: "Formation tag saved successfully",
        severity: "success",
      });

      setFormationTagDialogOpen(false);
      setCurrentFormationTag({ name: "", description: "" });
    } catch (error) {
      console.error("Error saving formation tag:", error);
      setAlert({
        open: true,
        message: "Failed to save formation tag",
        severity: "error",
      });
    }
  };

  // Handle save play tag
  const handleSavePlayTag = async () => {
    try {
      if (!currentPlayTag.name) {
        setAlert({
          open: true,
          message: "Name is required",
          severity: "error",
        });
        return;
      }

      await savePlayTag({
        id: currentPlayTag.id,
        name: currentPlayTag.name,
        description: currentPlayTag.description || null,
      });

      setAlert({
        open: true,
        message: "Play tag saved successfully",
        severity: "success",
      });

      setPlayTagDialogOpen(false);
      setCurrentPlayTag({ name: "", description: "" });
    } catch (error) {
      console.error("Error saving play tag:", error);
      setAlert({
        open: true,
        message: "Failed to save play tag",
        severity: "error",
      });
    }
  };

  // Handle save run concept
  const handleSaveRunConcept = async () => {
    try {
      await saveRunConcept({
        ...(currentRunConcept as OffensiveRunConcept),
      });

      setAlert({
        open: true,
        message: "Run concept saved successfully",
        severity: "success",
      });

      setRunConceptDialogOpen(false);
      setCurrentRunConcept({ 
        name: "", 
        description: "", 
        club: club,
        code_word_left: "",
        code_word_right: ""
      });
    } catch (error) {
      console.error("Error saving run concept:", error);
      setAlert({
        open: true,
        message: "Failed to save run concept",
        severity: "error",
      });
    }
  };

  // Handle save pass protection concept
  const handleSavePassProtectionConcept = async () => {
    try {
      await savePassProtectionConcept({
        ...(currentPassProtectionConcept as OffensivePassProtectionConcept),
      });

      setAlert({
        open: true,
        message: "Pass protection concept saved successfully",
        severity: "success",
      });

      setPassProtectionConceptDialogOpen(false);
      setCurrentPassProtectionConcept({ name: "", description: "", club: club.id });
    } catch (error) {
      console.error("Error saving pass protection concept:", error);
      setAlert({
        open: true,
        message: "Failed to save pass protection concept",
        severity: "error",
      });
    }
  };

  // Handle save pass concept
  const handleSavePassConcept = async () => {
    try {
      await savePassConcept({
        ...(currentPassConcept as OffensivePassConcept),
      });

      setAlert({
        open: true,
        message: "Pass concept saved successfully",
        severity: "success",
      });

      setPassConceptDialogOpen(false);
      setCurrentPassConcept({ name: "", description: "" });
    } catch (error) {
      console.error("Error saving pass concept:", error);
      setAlert({
        open: true,
        message: "Failed to save pass concept",
        severity: "error",
      });
    }
  };

  // Handle personnel group dialog
  const handleOpenPersonnelDialog = (group?: PersonnelGroup) => {
    if (group) {
      setCurrentPersonnelGroup(group);
    } else {
      setCurrentPersonnelGroup({ name: "", side: "OFFENSE" });
    }
    setPersonnelDialogOpen(true);
  };

  // Handle formation dialog
  const handleOpenFormationDialog = (formation?: SchemeFormation) => {
    if (formation) {
      setCurrentFormation(formation);
    } else {
      setCurrentFormation({ name: "", club: club });
    }
    setFormationDialogOpen(true);
  };

  // Handle formation tag dialog
  const handleOpenFormationTagDialog = (tag?: FormationTag) => {
    if (tag) {
      setCurrentFormationTag(tag);
    } else {
      setCurrentFormationTag({ name: "", description: "", club: club });
    }
    setFormationTagDialogOpen(true);
  };

  // Handle play tag dialog
  const handleOpenPlayTagDialog = (tag?: OffensivePlayTag) => {
    if (tag) {
      setCurrentPlayTag(tag);
    } else {
      setCurrentPlayTag({ name: "", description: "", club: club });
    }
    setPlayTagDialogOpen(true);
  };

  // Handle run concept dialog
  const handleOpenRunConceptDialog = (concept?: OffensiveRunConcept) => {
    if (concept) {
      setCurrentRunConcept(concept);
    } else {
      setCurrentRunConcept({ 
        name: "", 
        description: "", 
        club: club,
        code_word_left: "",
        code_word_right: ""
      });
    }
    setRunConceptDialogOpen(true);
  };

  // Handle pass concept dialog
  const handleOpenPassConceptDialog = (concept?: OffensivePassConcept) => {
    if (concept) {
      setCurrentPassConcept(concept);
    } else {
      setCurrentPassConcept({ name: "", description: "", club: club.id });
    }
    setPassConceptDialogOpen(true);
  };

  // Handle pass protection concept dialog
  const handleOpenPassProtectionConceptDialog = (
    concept?: OffensivePassProtectionConcept
  ) => {
    if (concept) {
      setCurrentPassProtectionConcept(concept);
    } else {
      setCurrentPassProtectionConcept({
        name: "",
        description: "",
        club: club.id,
      });
    }
    setPassProtectionConceptDialogOpen(true);
  };

  // Handle close alerts
  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  return (
    <Box
      sx={{
        p: 1,
        maxWidth: 1600,
        width: "100%",
        bgcolor: "#121212",
        color: "white",
        height: "100vh",
        boxSizing: "border-box",
      }}
    >
      {/* Tabs for configuration types */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: "rgba(123, 45, 58, 0.9)",
            },
            "& .MuiTab-root": {
              color: "rgba(255, 255, 255, 0.7)",
              "&.Mui-selected": {
                color: "white",
              },
            },
          }}
        >
          <Tab label="Personnel Groups" id="scheme-tab-0" />
          <Tab label="Formations" id="scheme-tab-1" />
          <Tab label="Run Concepts" id="scheme-tab-2" />
          <Tab label="Pass Pro Concepts" id="scheme-tab-3" />
          <Tab label="Pass Concepts" id="scheme-tab-4" />
          <Tab label="Formation Tags" id="scheme-tab-5" />
          <Tab label="Play Tags" id="scheme-tab-6" />
        </Tabs>
      </Box>

      {/* Main content area */}
      <Box
        sx={{
          bgcolor: "#1e1e1e",
          p: 2,
          borderRadius: 1,
          height: "auto",
          minHeight: "calc(100vh - 130px)",
          overflow: "visible",
        }}
      >
        {/* Personnel Groups Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenPersonnelDialog()}
              sx={{
                color: "white",
                bgcolor: "rgba(123, 45, 58, 0.9)",
                "&:hover": { bgcolor: "rgba(123, 45, 58, 0.7)" },
                fontWeight: 600,
              }}
            >
              Add Group
            </Button>
          </Box>

          <List sx={{ width: "100%", bgcolor: "transparent" }}>
            {offensivePersonnel.map((group) => (
              <ListItem
                key={group.id}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      onClick={() => handleOpenPersonnelDialog(group)}
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => group.id && deletePersonnelGroup(group.id)}
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                }
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
              >
                <ListItemText
                  primary={<Typography color="white">{group.name}</Typography>}
                />
              </ListItem>
            ))}
            {offensivePersonnel.length === 0 && (
              <ListItem
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
              >
                <ListItemText
                  primary={
                    <Typography color="rgba(255, 255, 255, 0.7)">
                      No personnel groups found
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </TabPanel>

        {/* Formations Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Formations</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenFormationDialog()}
              sx={{
                color: "white",
                bgcolor: "rgba(123, 45, 58, 0.9)",
                "&:hover": { bgcolor: "rgba(123, 45, 58, 0.7)" },
                fontWeight: 600,
              }}
            >
              Add Formation
            </Button>
          </Box>

          <List sx={{ width: "100%", bgcolor: "transparent" }}>
            {formations.map((formation) => (
              <ListItem
                key={formation.id}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      onClick={() => handleOpenFormationDialog(formation)}
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() =>
                        formation.id && deleteFormation(formation.id)
                      }
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                }
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
              >
                <ListItemText
                  primary={
                    <Typography color="white">{formation.name}</Typography>
                  }
                />
              </ListItem>
            ))}
            {formations.length === 0 && (
              <ListItem
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
              >
                <ListItemText
                  primary={
                    <Typography color="rgba(255, 255, 255, 0.7)">
                      No formations found
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </TabPanel>

        {/* Run Concepts Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Run Concepts</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenRunConceptDialog()}
              sx={{
                color: "white",
                bgcolor: "rgba(123, 45, 58, 0.9)",
                "&:hover": { bgcolor: "rgba(123, 45, 58, 0.7)" },
                fontWeight: 600,
              }}
            >
              Add Run Concept
            </Button>
          </Box>

          <List sx={{ width: "100%", bgcolor: "transparent" }}>
            {runConcepts.map((concept) => (
              <ListItem
                key={concept.id}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      onClick={() => handleOpenRunConceptDialog(concept)}
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => concept.id && deleteRunConcept(concept.id)}
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                }
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
              >
                <ListItemText
                  primary={<Typography color="white">{concept.code_word_left} / {concept.code_word_right}</Typography>}
                  secondary={
                    concept.description && (
                      <Typography color="rgba(255, 255, 255, 0.7)" variant="body2">
                        {concept.description}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            ))}
            {runConcepts.length === 0 && (
              <ListItem sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>
                <ListItemText
                  primary={
                    <Typography color="rgba(255, 255, 255, 0.7)">
                      No run concepts found
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </TabPanel>

        {/* Pass Protection Concepts Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Pass Protection Concepts</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenPassProtectionConceptDialog()}
              sx={{
                color: "white",
                bgcolor: "rgba(123, 45, 58, 0.9)",
                "&:hover": { bgcolor: "rgba(123, 45, 58, 0.7)" },
                fontWeight: 600,
              }}
            >
              Add Pass Protection Concept
            </Button>
          </Box>

          <List sx={{ width: "100%", bgcolor: "transparent" }}>
            {passProtectionConcepts.map((passPro) => (
              <ListItem
                key={passPro.id}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      onClick={() =>
                        handleOpenPassProtectionConceptDialog(passPro)
                      }
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() =>
                        passPro.id && deletePassProtectionConcept(passPro.id)
                      }
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                }
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
              >
                <ListItemText
                  primary={
                    <Typography color="white">{passPro.name}</Typography>
                  }
                  secondary={
                    passPro.description && (
                      <Typography
                        color="rgba(255, 255, 255, 0.7)"
                        variant="body2"
                      >
                        {passPro.description}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Pass Concepts Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Pass Concepts</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenPassConceptDialog()}
              sx={{
                color: "white",
                bgcolor: "rgba(123, 45, 58, 0.9)",
                "&:hover": { bgcolor: "rgba(123, 45, 58, 0.7)" },
                fontWeight: 600,
              }}
            >
              Add Pass Concept
            </Button>
          </Box>

          <List sx={{ width: "100%", bgcolor: "transparent" }}>
            {passConcepts.map((concept) => (
              <ListItem
                key={concept.id}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      onClick={() => handleOpenPassConceptDialog(concept)}
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() =>
                        concept.id && deletePassConcept(concept.id)
                      }
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                }
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
              >
                <ListItemText
                  primary={
                    <Typography color="white">{concept.name}</Typography>
                  }
                  secondary={
                    concept.description && (
                      <Typography
                        color="rgba(255, 255, 255, 0.7)"
                        variant="body2"
                      >
                        {concept.description}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            ))}
            {passConcepts.length === 0 && (
              <ListItem
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
              >
                <ListItemText
                  primary={
                    <Typography color="rgba(255, 255, 255, 0.7)">
                      No pass concepts found
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </TabPanel>

        {/* Formation Tags Tab */}
        <TabPanel value={tabValue} index={5}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Formation Tags</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenFormationTagDialog()}
              sx={{
                color: "white",
                bgcolor: "rgba(123, 45, 58, 0.9)",
                "&:hover": { bgcolor: "rgba(123, 45, 58, 0.7)" },
                fontWeight: 600,
              }}
            >
              Add Tag
            </Button>
          </Box>

          <List sx={{ width: "100%", bgcolor: "transparent" }}>
            {formationTags.map((tag) => (
              <ListItem
                key={tag.id}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      onClick={() => handleOpenFormationTagDialog(tag)}
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => tag.id && deleteFormationTag(tag.id)}
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                }
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
              >
                <ListItemText
                  primary={<Typography color="white">{tag.name}</Typography>}
                  secondary={
                    tag.description && (
                      <Typography
                        color="rgba(255, 255, 255, 0.7)"
                        variant="body2"
                      >
                        {tag.description}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Play Tags Tab */}
        <TabPanel value={tabValue} index={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Play Tags</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenPlayTagDialog()}
              sx={{
                color: "white",
                bgcolor: "rgba(123, 45, 58, 0.9)",
                "&:hover": { bgcolor: "rgba(123, 45, 58, 0.7)" },
                fontWeight: 600,
              }}
            >
              Add Tag
            </Button>
          </Box>

          <List sx={{ width: "100%", bgcolor: "transparent" }}>
            {offensivePlayTags.map((tag) => (
              <ListItem
                key={tag.id}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      onClick={() => handleOpenPlayTagDialog(tag)}
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => tag.id && deletePlayTag(tag.id)}
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                }
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
              >
                <ListItemText
                  primary={<Typography color="white">{tag.name}</Typography>}
                  secondary={
                    tag.description && (
                      <Typography
                        color="rgba(255, 255, 255, 0.7)"
                        variant="body2"
                      >
                        {tag.description}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            ))}
          </List>
        </TabPanel>
      </Box>

      {/* Personnel Group Dialog */}
      <Dialog
        open={personnelDialogOpen}
        onClose={() => setPersonnelDialogOpen(false)}
      >
        <DialogTitle>
          {currentPersonnelGroup.id
            ? "Edit Personnel Group"
            : "Add Personnel Group"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={currentPersonnelGroup.name}
              onChange={(e) =>
                setCurrentPersonnelGroup({
                  ...currentPersonnelGroup,
                  name: e.target.value,
                })
              }
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPersonnelDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSavePersonnelGroup}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Formation Dialog */}
      <Dialog
        open={formationDialogOpen}
        onClose={() => setFormationDialogOpen(false)}
      >
        <DialogTitle>
          {currentFormation.id ? "Edit Formation" : "Add Formation"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={currentFormation.name}
              onChange={(e) =>
                setCurrentFormation({
                  ...currentFormation,
                  name: e.target.value,
                })
              }
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormationDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveFormation}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Formation Tag Dialog */}
      <Dialog
        open={formationTagDialogOpen}
        onClose={() => setFormationTagDialogOpen(false)}
      >
        <DialogTitle>
          {currentFormationTag.id ? "Edit Formation Tag" : "Add Formation Tag"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={currentFormationTag.name}
              onChange={(e) =>
                setCurrentFormationTag({
                  ...currentFormationTag,
                  name: e.target.value,
                })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Description"
              value={currentFormationTag.description || ""}
              onChange={(e) =>
                setCurrentFormationTag({
                  ...currentFormationTag,
                  description: e.target.value,
                })
              }
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormationTagDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveFormationTag}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Play Tag Dialog */}
      <Dialog
        open={playTagDialogOpen}
        onClose={() => setPlayTagDialogOpen(false)}
      >
        <DialogTitle>
          {currentPlayTag.id ? "Edit Play Tag" : "Add Play Tag"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={currentPlayTag.name}
              onChange={(e) =>
                setCurrentPlayTag({ ...currentPlayTag, name: e.target.value })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Description"
              value={currentPlayTag.description || ""}
              onChange={(e) =>
                setCurrentPlayTag({
                  ...currentPlayTag,
                  description: e.target.value,
                })
              }
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlayTagDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSavePlayTag}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Run Concept Dialog */}
      <Dialog
        open={runConceptDialogOpen}
        onClose={() => setRunConceptDialogOpen(false)}
      >
        <DialogTitle>
          {currentRunConcept.id ? "Edit Run Concept" : "Add Run Concept"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={currentRunConcept.name}
              onChange={(e) =>
                setCurrentRunConcept({
                  ...currentRunConcept,
                  name: e.target.value,
                })
              }
              margin="normal"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Code Word Left"
                value={currentRunConcept.code_word_left || ""}
                onChange={(e) =>
                  setCurrentRunConcept({
                    ...currentRunConcept,
                    code_word_left: e.target.value,
                  })
                }
                margin="normal"
              />
              <TextField
                fullWidth
                label="Code Word Right"
                value={currentRunConcept.code_word_right || ""}
                onChange={(e) =>
                  setCurrentRunConcept({
                    ...currentRunConcept,
                    code_word_right: e.target.value,
                  })
                }
                margin="normal"
              />
            </Box>
            <TextField
              fullWidth
              label="Description"
              value={currentRunConcept.description || ""}
              onChange={(e) =>
                setCurrentRunConcept({
                  ...currentRunConcept,
                  description: e.target.value,
                })
              }
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunConceptDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveRunConcept}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pass Concept Dialog */}
      <Dialog
        open={passConceptDialogOpen}
        onClose={() => setPassConceptDialogOpen(false)}
      >
        <DialogTitle>
          {currentPassConcept.id ? "Edit Pass Concept" : "Add Pass Concept"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={currentPassConcept.name}
              onChange={(e) =>
                setCurrentPassConcept({
                  ...currentPassConcept,
                  name: e.target.value,
                })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Description"
              value={currentPassConcept.description || ""}
              onChange={(e) =>
                setCurrentPassConcept({
                  ...currentPassConcept,
                  description: e.target.value,
                })
              }
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPassConceptDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePassConcept}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pass Protection Concept Dialog */}
      <Dialog
        open={passProtectionConceptDialogOpen}
        onClose={() => setPassProtectionConceptDialogOpen(false)}
      >
        <DialogTitle>
          {currentPassProtectionConcept.id ? "Edit Pass Protection Concept" : "Add Pass Protection Concept"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={currentPassProtectionConcept.name}
              onChange={(e) =>
                setCurrentPassProtectionConcept({
                  ...currentPassProtectionConcept,
                  name: e.target.value,
                })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Description"
              value={currentPassProtectionConcept.description || ""}
              onChange={(e) =>
                setCurrentPassProtectionConcept({
                  ...currentPassProtectionConcept,
                  description: e.target.value,
                })
              }
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPassProtectionConceptDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePassProtectionConcept}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
